import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { initGA } from './analytics';
import Navigation from './components/Navigation';
import FeedbackBanner from './components/FeedbackBanner';
import RouteTracker from './components/RouteTracker';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { WichListProvider } from './hooks/useWichList';
import Home from './pages/Home';

const Login = React.lazy(() => import('./pages/Login'));
const AuthVerify = React.lazy(() => import('./pages/AuthVerify'));
const Search = React.lazy(() => import('./pages/Search'));
const SandwichDetail = React.lazy(() => import('./pages/SandwichDetail'));
const RestaurantDetail = React.lazy(() => import('./pages/RestaurantDetail'));
const ReviewRedirect = React.lazy(() => import('./pages/ReviewRedirect'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const ListDetail = React.lazy(() => import('./pages/ListDetail'));
const Upload = React.lazy(() => import('./pages/Upload'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Settings = React.lazy(() => import('./pages/Settings'));

const RouteLoadingFallback = () => (
  <div className="flex justify-center items-center py-12 text-gray-600">Loading...</div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }
  return user ? children : <Navigate to="/login" />;
};

function App() {
  useEffect(() => {
    initGA();
  }, []);

  return (
    <AuthProvider>
      <WichListProvider>
        <ToastProvider>
          <div className="App">
            <BrowserRouter>
              <RouteTracker />
              <FeedbackBanner />
              <Navigation />
              <Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/auth/verify" element={<AuthVerify />} />
                  <Route path="/search" element={<Search />} />
                  <Route path="/review/:id" element={<ReviewRedirect />} />
                  <Route path="/sandwich/:id" element={<SandwichDetail key={window.location.pathname} />} />
                  <Route path="/restaurant/:id" element={<RestaurantDetail />} />
                  <Route path="/profile/:id" element={<ProfilePage />} />
                  <Route path="/list/:id" element={<ListDetail />} />
                  <Route
                    path="/upload"
                    element={
                      <ProtectedRoute>
                        <Upload />
                      </ProtectedRoute>
                    }
                  />
                  {/* Legal Pages - Public Access */}
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  {/* Settings - Protected */}
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </div>
        </ToastProvider>
      </WichListProvider>
    </AuthProvider>
  );
}

export default App;
